<template>
  <div class="cat-page">

    <!-- Hero -->
    <section class="cat-hero">
      <div class="cat-hero-inner">
        <span class="cat-eyebrow">Assortiment</span>
        <h1 class="cat-hero-title">Alle categorieën</h1>
        <p class="cat-hero-sub">Blader door ons volledige aanbod laadpalen, batterijen en energieproducten.</p>
      </div>
    </section>

    <div class="cat-wrap">

      <!-- Keuzehulp -->
      <router-link to="/keuzehulp" class="cat-kh">
        <span class="cat-kh-icon"><i class="bi bi-compass"></i></span>
        <span class="cat-kh-text">
          <strong>Moeite met kiezen?</strong>
          <span>Doe de keuzehulp en vind het juiste product in 1 minuut.</span>
        </span>
        <span class="cat-kh-arrow">Start <i class="bi bi-arrow-right"></i></span>
      </router-link>

      <!-- Loading -->
      <div v-if="loading" class="cat-loading">
        <div class="cat-spinner"></div>
        <p>Categorieën laden…</p>
      </div>

      <!-- Layout -->
      <div v-else class="cat-browser">

        <!-- ── Sidebar: lijst met mini-afbeeldingen / iconen ── -->
        <aside class="cat-sidebar">
          <p class="cat-sidebar-label">Categorieën</p>
          <nav>
            <button
              v-for="(cat, i) in mainCategories"
              :key="cat.id"
              class="cat-sidebar-item"
              :class="{ 'is-active': activeIndex === i }"
              @click="goTo(i)"
            >
              <!-- Mini-afbeelding of gekleurde icoon-badge -->
              <span class="cat-sb-thumb" :style="{ background: cardColors[i % cardColors.length] }">
                <img v-if="cat.image" :src="sized(cat.image.src, 80)" :alt="cat.name" loading="lazy">
                <i v-else :class="['bi', getCategoryIcon(cat.slug)]"></i>
              </span>
              <span class="cat-sb-name">{{ cat.name }}</span>
              <i class="bi bi-chevron-right cat-sb-arrow"></i>
            </button>
          </nav>
        </aside>

        <!-- ── Carousel rechts ── -->
        <div class="cat-carousel-wrap">
          <!-- Pijl links -->
          <button
            class="cat-arrow cat-arrow--prev"
            :disabled="carouselOffset === 0"
            @click="scrollCarousel(-1)"
            aria-label="Vorige"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <!-- Track -->
          <div class="cat-carousel-viewport" ref="viewport">
            <div class="cat-carousel-track" ref="track">
              <router-link
                v-for="(cat, i) in mainCategories"
                :key="cat.id"
                :to="`/categorie/${cat.slug}`"
                class="cat-card"
                :data-index="i"
                :style="{ background: cardColors[i % cardColors.length] }"
              >
                <!-- Decoratieve cirkel achtergrond -->
                <span class="cat-card-blob"></span>

                <!-- Header -->
                <div class="cat-card-header">
                  <h3 class="cat-card-name">{{ cat.name }}</h3>
                  <p v-if="cat.description" class="cat-card-desc">{{ stripHtml(cat.description) }}</p>
                </div>

                <!-- Productfoto -->
                <div class="cat-card-media">
                  <img v-if="cat.image" :src="sized(cat.image.src, 400)" :alt="cat.name" loading="lazy">
                  <i v-else :class="['bi cat-card-fallback', getCategoryIcon(cat.slug)]"></i>
                </div>

                <!-- CTA -->
                <span class="cat-card-btn">Bekijk <i class="bi bi-arrow-right"></i></span>
              </router-link>
            </div>
          </div>

          <!-- Pijl rechts -->
          <button
            class="cat-arrow cat-arrow--next"
            :disabled="carouselAtEnd"
            @click="scrollCarousel(1)"
            aria-label="Volgende"
          >
            <i class="bi bi-chevron-right"></i>
          </button>

          <!-- Dots -->
          <div class="cat-dots">
            <button
              v-for="(cat, i) in mainCategories"
              :key="cat.id"
              class="cat-dot"
              :class="{ 'is-active': activeIndex === i }"
              @click="goTo(i)"
              :aria-label="cat.name"
            ></button>
          </div>
        </div>

      </div>
    </div>

    <!-- CTA -->
    <section class="cat-cta-section">
      <div class="cat-wrap">
        <div class="cat-cta-inner">
          <div>
            <h2 class="cat-cta-title">Hulp nodig bij je keuze?</h2>
            <p class="cat-cta-sub">Onze energiespecialisten helpen je gratis de juiste oplossing te vinden.</p>
          </div>
          <div class="cat-cta-actions">
            <a href="mailto:info@plug2charge.nl" class="cat-btn cat-btn--primary"><i class="bi bi-envelope"></i> Mail ons</a>
            <a href="/klantenservice#livechat" class="cat-btn cat-btn--ghost"><i class="bi bi-chat-dots"></i> Live chat</a>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useProductStore } from '@stores/products'

const productStore = useProductStore()

const loading    = computed(() => productStore.loading)
const categories = computed(() => productStore.categories || [])

const mainCategories = computed(() =>
  [...categories.value]
    .filter(c => c.parent === 0 && c.slug !== 'uncategorized' && c.count > 0)
    .sort((a, b) => b.count - a.count)
)

// ── Carousel state ──────────────────────────────
const viewport       = ref(null)
const track          = ref(null)
const activeIndex    = ref(0)
const carouselOffset = ref(0)
const carouselAtEnd  = ref(false)

const cardColors = [
  '#BFA882', '#8B96BE', '#7BAF94', '#BE8B82',
  '#8B7DBE', '#6E9EBE', '#BE9E7D', '#7EBE8B',
]

function getCardWidth() {
  if (!track.value) return 280
  const card = track.value.querySelector('.cat-card')
  if (!card) return 280
  const style = window.getComputedStyle(track.value)
  const gap = parseFloat(style.gap) || 16
  return card.offsetWidth + gap
}

function updateEndState() {
  if (!viewport.value) return
  const vp = viewport.value
  carouselAtEnd.value = (vp.scrollLeft + vp.offsetWidth) >= (vp.scrollWidth - 4)
}

function scrollCarousel(dir) {
  if (!viewport.value) return
  const w = getCardWidth()
  viewport.value.scrollBy({ left: dir * w, behavior: 'smooth' })
  setTimeout(syncActiveFromScroll, 350)
}

function goTo(index) {
  if (!viewport.value || !track.value) return
  activeIndex.value = index
  const cards = track.value.querySelectorAll('.cat-card')
  if (cards[index]) {
    cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  }
}

function syncActiveFromScroll() {
  if (!viewport.value || !track.value) return
  const vp = viewport.value
  carouselOffset.value = vp.scrollLeft
  updateEndState()
  const w = getCardWidth()
  activeIndex.value = Math.round(vp.scrollLeft / w)
}

let scrollTimeout = null
function onScroll() {
  clearTimeout(scrollTimeout)
  scrollTimeout = setTimeout(syncActiveFromScroll, 80)
}

// ── Helpers ──────────────────────────────────────
const sized = (url, w = 400) => {
  if (!url) return url
  const i = url.indexOf('/wp-content/')
  return i === -1 ? url : `${url.slice(i)}?w=${w}`
}

const getCategoryIcon = (slug) => ({
  'zonnepanelen':          'bi-sun',
  'balkon-zonnepanelen':   'bi-sun',
  'complete-sets':         'bi-box-seam',
  'laadpalen':             'bi-ev-station',
  'thuislaadpalen':        'bi-ev-station',
  'slimme-laadpalen':      'bi-lightning-charge',
  '3-fase-laadpalen':      'bi-lightning-charge',
  'laadkabels':            'bi-plug',
  'wallboxen':             'bi-lightning-charge',
  'micro-omvormers':       'bi-gear',
  'omvormers':             'bi-gear',
  'thuisbatterijen':       'bi-battery-charging',
  'all-in-one-batterijen': 'bi-battery-full',
  'stekkerbatterij':       'bi-battery',
  'draagbare-batterijen':  'bi-battery',
  'accu':                  'bi-battery',
  'montage-materiaal':     'bi-hammer',
  'kabels':                'bi-plug',
  'accessoires':           'bi-gear',
}[slug] || 'bi-folder')

const stripHtml = (html) => {
  if (!html) return ''
  const t = html.replace(/<[^>]*>/g, '').trim()
  return t.length > 75 ? t.slice(0, 75) + '…' : t
}

onMounted(async () => {
  productStore.fetchCategories()
  if (typeof document !== 'undefined') document.title = 'Alle categorieën | Plug2Charge.nl'

  await nextTick()
  if (viewport.value) {
    viewport.value.addEventListener('scroll', onScroll, { passive: true })
    updateEndState()
  }
})

onUnmounted(() => {
  if (viewport.value) viewport.value.removeEventListener('scroll', onScroll)
  clearTimeout(scrollTimeout)
})
</script>

<style scoped>
.cat-page { background: #f6f7f9; min-height: 100vh; }
.cat-wrap { max-width: 1300px; margin: 0 auto; padding: 0 1.5rem; }

/* Hero */
.cat-hero { background: #111; color: #fff; padding: 3rem 0; }
.cat-hero-inner { max-width: 1300px; margin: 0 auto; padding: 0 1.5rem; }
.cat-eyebrow {
  display: inline-block;
  background: rgba(255,213,0,0.15);
  color: #FFD500;
  font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 0.3rem 1rem; border-radius: 50px;
  border: 1px solid rgba(255,213,0,0.3); margin-bottom: 1rem;
}
.cat-hero-title { font-size: clamp(1.75rem, 4vw, 2.5rem); font-weight: 900; letter-spacing: -0.02em; margin: 0 0 0.5rem; }
.cat-hero-sub { font-size: 1rem; color: #aaa; margin: 0; }

/* Keuzehulp */
.cat-kh {
  display: flex; align-items: center; gap: 0.9rem;
  margin: 1.5rem 0;
  padding: 0.85rem 1.1rem;
  background: #fff; border: 1px solid #e9ecef; border-radius: 14px;
  text-decoration: none;
  transition: border-color 150ms cubic-bezier(0.23,1,0.32,1);
}
.cat-kh:hover { border-color: #FFD500; }
.cat-kh-icon { flex-shrink: 0; width: 38px; height: 38px; border-radius: 50%; background: #FFF6CC; display: flex; align-items: center; justify-content: center; color: #b8860b; font-size: 1.05rem; }
.cat-kh-text { flex: 1; line-height: 1.3; }
.cat-kh-text strong { display: block; font-size: 0.88rem; color: #111; }
.cat-kh-text span { display: block; font-size: 0.78rem; color: #777; }
.cat-kh-arrow { flex-shrink: 0; color: #b8860b; font-weight: 600; font-size: 0.82rem; display: inline-flex; align-items: center; gap: 0.25rem; }

/* Loading */
.cat-loading { display: flex; flex-direction: column; align-items: center; padding: 4rem 2rem; color: #888; }
.cat-spinner { width: 3rem; height: 3rem; border: 4px solid #eee; border-top-color: #FFD500; border-radius: 50%; animation: cat-spin 1s linear infinite; margin-bottom: 1rem; }
@keyframes cat-spin { to { transform: rotate(360deg); } }

/* ── Main browser layout ── */
.cat-browser {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  align-items: flex-start;
}

/* ── Sidebar ── */
.cat-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 18px;
  padding: 1.1rem 0.75rem;
  position: sticky;
  top: 80px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #eee transparent;
}
.cat-sidebar::-webkit-scrollbar { width: 3px; }
.cat-sidebar::-webkit-scrollbar-thumb { background: #eee; border-radius: 2px; }

.cat-sidebar-label {
  font-size: 0.63rem; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: #bbb;
  padding: 0 0.5rem 0.6rem; margin: 0;
}

.cat-sidebar-item {
  display: flex; align-items: center; gap: 0.6rem;
  width: 100%; padding: 0.45rem 0.5rem;
  border: none; background: none; cursor: pointer; text-align: left;
  border-radius: 10px;
  transition: background 120ms cubic-bezier(0.23,1,0.32,1);
  margin-bottom: 2px;
}
.cat-sidebar-item:hover,
.cat-sidebar-item.is-active { background: #fffbeb; }
.cat-sidebar-item.is-active .cat-sb-name { color: #111; font-weight: 700; }
.cat-sidebar-item.is-active .cat-sb-arrow { color: #b8860b; }

/* Mini thumbnail / icoon badge */
.cat-sb-thumb {
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 9px;
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.cat-sb-thumb img { width: 100%; height: 100%; object-fit: cover; }
.cat-sb-thumb .bi { color: rgba(255,255,255,0.9); font-size: 1rem; }

.cat-sb-name { flex: 1; min-width: 0; font-size: 0.8rem; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cat-sb-arrow { flex-shrink: 0; font-size: 0.55rem; color: #ccc; transition: color 120ms, transform 120ms; }
.cat-sidebar-item:hover .cat-sb-arrow { color: #b8860b; transform: translateX(2px); }

/* ── Carousel ── */
.cat-carousel-wrap {
  flex: 1; min-width: 0;
  position: relative;
}

.cat-carousel-viewport {
  overflow-x: auto;
  overflow-y: visible;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  padding-bottom: 2px; /* prevent clip of box-shadow */
}
.cat-carousel-viewport::-webkit-scrollbar { display: none; }

.cat-carousel-track {
  display: flex;
  gap: 1rem;
  width: max-content;
  padding: 4px 2px 12px; /* room for shadow */
}

/* Cards */
.cat-card {
  width: 240px;
  flex-shrink: 0;
  border-radius: 18px;
  padding: 1.25rem 1.25rem 1rem;
  display: flex; flex-direction: column;
  text-decoration: none;
  position: relative; overflow: hidden;
  scroll-snap-align: start;
  min-height: 320px;
  transition: transform 200ms cubic-bezier(0.23,1,0.32,1), box-shadow 200ms;
}
.cat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(0,0,0,0.16);
}

/* Decoratieve blob achtergrond */
.cat-card-blob {
  position: absolute;
  bottom: -30px; right: -30px;
  width: 160px; height: 160px;
  border-radius: 50%;
  background: rgba(255,255,255,0.12);
  pointer-events: none;
}

.cat-card-header { position: relative; z-index: 2; }
.cat-card-name { font-size: 1.15rem; font-weight: 800; color: #fff; margin: 0 0 0.35rem; line-height: 1.2; text-shadow: 0 1px 4px rgba(0,0,0,0.2); }
.cat-card-desc { font-size: 0.78rem; color: rgba(255,255,255,0.8); margin: 0; line-height: 1.45; }

.cat-card-media {
  flex: 1;
  display: flex; align-items: center; justify-content: center;
  padding: 0.75rem 0;
  position: relative; z-index: 2;
}
.cat-card-media img {
  width: 100%; max-height: 150px; object-fit: contain;
  filter: drop-shadow(0 8px 18px rgba(0,0,0,0.25));
  transition: transform 220ms cubic-bezier(0.23,1,0.32,1);
}
.cat-card:hover .cat-card-media img { transform: translateY(-4px) scale(1.04); }
.cat-card-fallback { font-size: 4rem; color: rgba(255,255,255,0.35); }

.cat-card-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  align-self: flex-start;
  position: relative; z-index: 2;
  background: rgba(255,255,255,0.18);
  border: 1.5px solid rgba(255,255,255,0.65);
  color: #fff; font-weight: 700; font-size: 0.82rem;
  padding: 0.55rem 1.1rem; border-radius: 9px;
  backdrop-filter: blur(4px);
  transition: background 150ms, border-color 150ms;
}
.cat-card:hover .cat-card-btn { background: rgba(255,255,255,0.3); border-color: rgba(255,255,255,0.95); }

/* Navigatiepijlen */
.cat-arrow {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 38px; height: 38px; border-radius: 50%;
  background: #fff; border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; color: #333; z-index: 10;
  transition: background 120ms, border-color 120ms, opacity 120ms;
}
.cat-arrow:hover { background: #fffbeb; border-color: #FFD500; color: #111; }
.cat-arrow:disabled { opacity: 0.3; cursor: default; pointer-events: none; }
.cat-arrow--prev { left: -20px; }
.cat-arrow--next { right: -20px; }

/* Dots */
.cat-dots {
  display: flex; justify-content: center; gap: 6px;
  margin-top: 0.75rem;
}
.cat-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #ddd; border: none; cursor: pointer;
  padding: 0;
  transition: background 180ms, transform 180ms, width 180ms;
}
.cat-dot.is-active { background: #FFD500; width: 20px; border-radius: 4px; }

/* ── CTA ── */
.cat-cta-section { padding: 0 0 3rem; }
.cat-cta-inner {
  display: flex; align-items: center; justify-content: space-between; gap: 2rem;
  background: #fff; border: 1px solid #eee; border-radius: 18px; padding: 2rem 2.25rem;
}
.cat-cta-title { font-size: 1.4rem; font-weight: 900; color: #111; margin: 0 0 0.35rem; }
.cat-cta-sub { font-size: 0.95rem; color: #666; margin: 0; max-width: 460px; }
.cat-cta-actions { display: flex; gap: 0.75rem; flex-shrink: 0; }
.cat-btn {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.8rem 1.4rem; border-radius: 10px;
  font-weight: 700; font-size: 0.875rem; text-decoration: none;
  transition: background 150ms, border-color 150ms, transform 120ms;
}
.cat-btn--primary { background: #FFD500; color: #111; }
.cat-btn--primary:hover { background: #ffb800; transform: translateY(-1px); }
.cat-btn--ghost { background: #fff; color: #333; border: 1px solid #e5e7eb; }
.cat-btn--ghost:hover { border-color: #FFD500; color: #111; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .cat-sidebar { display: none; }
  .cat-browser { gap: 0; }
  .cat-arrow { display: none; }
}
@media (max-width: 640px) {
  .cat-card { width: 190px; min-height: 270px; }
  .cat-cta-inner { flex-direction: column; align-items: flex-start; }
  .cat-cta-actions { width: 100%; }
  .cat-btn { flex: 1; justify-content: center; }
}
@media (prefers-reduced-motion: reduce) {
  .cat-card, .cat-card-media img, .cat-sidebar-item { transition: none; }
}
</style>
